export interface SessionState {
  userEmail: string | null;
  userName: string | null;
  accessToken: string | null;
  accessTokenExpiresAt: number | null;
  isPremium: boolean;
}

const LOCAL_WIX_TOKENS_STORAGE_KEY = 'qclLocalWixTokens';

interface FetchSessionOptions {
  refresh?: boolean;
}

const toNullableString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

export const resolveAccessTokenValue = (input: unknown): string | null => {
  if (typeof input === 'string') {
    const raw = input.trim();
    if (!raw) return null;

    if (/^Bearer\s+/i.test(raw)) {
      const bearerToken = raw.replace(/^Bearer\s+/i, '').trim();
      return bearerToken || null;
    }

    try {
      return resolveAccessTokenValue(JSON.parse(raw));
    } catch {
      return raw;
    }
  }

  if (!input || typeof input !== 'object') return null;

  const record = input as Record<string, unknown>;
  if ('accessToken' in record) {
    const nestedToken = resolveAccessTokenValue(record.accessToken);
    if (nestedToken) return nestedToken;
  }
  if ('access_token' in record) {
    const nestedToken = resolveAccessTokenValue(record.access_token);
    if (nestedToken) return nestedToken;
  }
  if ('value' in record) {
    const directValue = resolveAccessTokenValue(record.value);
    if (directValue) return directValue;
  }

  return null;
};

const parseSessionResponse = (payload: any): SessionState | null => {
  if (!payload || payload.ok !== true) return null;

  return {
    userEmail: toNullableString(payload.userEmail),
    userName: toNullableString(payload.userName),
    accessToken: toNullableString(payload.accessToken),
    accessTokenExpiresAt:
      typeof payload.accessTokenExpiresAt === 'number' && Number.isFinite(payload.accessTokenExpiresAt)
        ? payload.accessTokenExpiresAt
        : null,
    isPremium: Boolean(payload.isPremium),
  };
};

const canUseLocalStorage = (): boolean => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export const persistLocalWixTokens = (tokens: unknown): void => {
  if (!canUseLocalStorage() || !tokens) return;

  try {
    window.localStorage.setItem(LOCAL_WIX_TOKENS_STORAGE_KEY, JSON.stringify(tokens));
  } catch (error) {
    console.warn('Failed to persist local Wix tokens:', error);
  }
};

export const getStoredLocalWixTokens = (): unknown | null => {
  if (!canUseLocalStorage()) return null;

  try {
    const raw = window.localStorage.getItem(LOCAL_WIX_TOKENS_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.warn('Failed to read local Wix tokens:', error);
    return null;
  }
};

export const getStoredLocalAccessToken = (): string | null =>
  resolveAccessTokenValue(getStoredLocalWixTokens());

export const clearLocalWixTokens = (): void => {
  if (!canUseLocalStorage()) return;

  try {
    window.localStorage.removeItem(LOCAL_WIX_TOKENS_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear local Wix tokens:', error);
  }
};

export const fetchServerSession = async (
  options: FetchSessionOptions = {}
): Promise<SessionState | null> => {
  const query = options.refresh ? '?refresh=1' : '';
  const response = await fetch(`/api/auth/session${query}`, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
  });

  if (!response.ok) return null;

  const payload = await response.json().catch(() => null);
  return parseSessionResponse(payload);
};

export const startServerSession = async (tokens: unknown): Promise<SessionState | null> => {
  const response = await fetch('/api/auth/session', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tokens }),
  });

  if (!response.ok) return null;

  const payload = await response.json().catch(() => null);
  return parseSessionResponse(payload);
};

export const clearServerSession = async (): Promise<void> => {
  await fetch('/api/auth/session', {
    method: 'DELETE',
    credentials: 'include',
  }).catch(() => undefined);
};
